import { z } from "zod";

/*
 * Zeichenprogramm für Installationspläne (Zusatzabo 1, nur Windows-App).
 *
 * Alle Längen sind Zentimeter in der Wirklichkeit. Symbole und Schrift haben auf dem Papier eine feste Größe
 * (unabhängig vom Maßstab), so wie man es von Plänen kennt. Die Schaltzeichen sind selbst gezeichnete, einfache
 * Formen in einem Raster von -10 bis 10; es werden keine Symbolbibliotheken anderer Hersteller verwendet.
 */

export const PLAN_SHEETS = {
  A4: [297, 210],
  A3: [420, 297],
  A2: [594, 420],
  A1: [841, 594],
} as const;
export type PlanSheet = keyof typeof PLAN_SHEETS;
export const PLAN_SCALES = [20, 25, 50, 75, 100, 200] as const;

/** Größe eines Symbols auf dem Papier in Millimetern. */
export const SYMBOL_MM = 6;
/** Schrifthöhe auf dem Papier in Millimetern. */
export const TEXT_MM = 2.8;

export const OPENING_KINDS = {
  fenster: { name: "Fenster", w: 100 },
  tuer: { name: "Tür", w: 90 },
  balkontuer: { name: "Balkon-/Terrassentür", w: 100 },
  doppeltuer: { name: "Doppeltür", w: 180 },
  durchgang: { name: "Durchgang ohne Tür", w: 100 },
} as const;
export type OpeningKind = keyof typeof OPENING_KINDS;

export const PLAN_DISCLAIMER =
  "Planungshilfe. Die Darstellung orientiert sich an den üblichen Regeln für Elektropläne im Wohnungsbau (z. B. DIN 18015). " +
  "Ob der Plan im Einzelfall normgerecht und vollständig ist, muss die planende Fachkraft selbst prüfen.";

/* ---------- Symbole ---------- */

export type Prim =
  | { t: "l"; x1: number; y1: number; x2: number; y2: number }
  | { t: "c"; x: number; y: number; r: number; f?: boolean }
  | { t: "r"; x: number; y: number; w: number; h: number; f?: boolean }
  | { t: "p"; pts: [number, number][]; closed?: boolean; f?: boolean }
  | { t: "tx"; x: number; y: number; s: string; size: number };

export interface SymbolDef {
  id: string;
  name: string;
  category: string;
  prims: Prim[];
}

const arc = (cx: number, cy: number, r: number, a0: number, a1: number, n = 12): [number, number][] =>
  Array.from({ length: n + 1 }, (_, i) => {
    const a = ((a0 + ((a1 - a0) * i) / n) * Math.PI) / 180;
    return [round(cx + r * Math.cos(a)), round(cy + r * Math.sin(a))] as [number, number];
  });
const round = (v: number) => Math.round(v * 100) / 100;
const line = (x1: number, y1: number, x2: number, y2: number): Prim => ({ t: "l", x1, y1, x2, y2 });
const circle = (x: number, y: number, r: number, f = false): Prim => ({ t: "c", x, y, r, f });
const text = (s: string, size = 6, x = 0, y = 0): Prim => ({ t: "tx", x, y, s, size });

const rotPt = (x: number, y: number, deg: number, cx = 0, cy = 0): [number, number] => {
  const r = (deg * Math.PI) / 180;
  const dx = x - cx;
  const dy = y - cy;
  return [round(cx + dx * Math.cos(r) - dy * Math.sin(r)), round(cy + dx * Math.sin(r) + dy * Math.cos(r))];
};

/**
 * Schalter: kleiner Kreis, davon ein Strich schräg nach oben mit „Haken“ am Ende. Die Zahl der kurzen Querstriche am Ende
 * zeigt, wie viele Schalter (1 = Ausschalter, 2 = Serienschalter). `mirror` spiegelt den Haken nach links, `turn` dreht um den Kreis.
 */
const hook = (flags: number, turn = 0, mirror = false): Prim[] => {
  const dir: [number, number] = [0.5, -0.866];
  const pt = (t: number): [number, number] => [dir[0] * t, dir[1] * t];
  const m = (q: [number, number]): [number, number] => (mirror ? [-q[0], q[1]] : q);
  const out: Prim[] = [];
  const a = m(pt(2.8));
  const b = m(pt(9));
  out.push(line(...rotPt(a[0], a[1], turn), ...rotPt(b[0], b[1], turn)));
  for (let i = 0; i < flags; i++) {
    const from = m(pt(9 - i * 2.2));
    const to: [number, number] = [from[0] + (mirror ? -1 : 1) * 4.5, from[1] + 1.8];
    out.push(line(...rotPt(from[0], from[1], turn), ...rotPt(to[0], to[1], turn)));
  }
  return out;
};
/** Kleiner Schaltkreis; `cross` = Kontrolllicht (Kreuz im Kreis). */
const switchCircle = (cross = false): Prim[] => [
  circle(0, 0, 2.8),
  ...(cross ? [line(-2, -2, 2, 2), line(-2, 2, 2, -2)] : []),
];
const shift = (prims: Prim[], dx: number, dy: number): Prim[] =>
  prims.map((p) => {
    if (p.t === "l") return { ...p, x1: round(p.x1 + dx), y1: round(p.y1 + dy), x2: round(p.x2 + dx), y2: round(p.y2 + dy) };
    if (p.t === "c" || p.t === "r" || p.t === "tx") return { ...p, x: round(p.x + dx), y: round(p.y + dy) };
    return { ...p, pts: p.pts.map((q) => [round(q[0] + dx), round(q[1] + dy)] as [number, number]) };
  });

/**
 * Steckdose in der üblichen Darstellung: waagerechte Linie, darunter ein Halbbogen, darüber der Strich zur Wand (Mehrfach: mehrere Striche).
 * Der Strich zeigt in der Zeichnung zur Wand. Mit `feet`: kleine Füße am Bogen für den Schutzkontakt.
 */
const socket = (stems: number[] = [0], feet = true): Prim[] =>
  shift(
    [
      line(-9, 0, 9, 0),
      ...stems.map((x) => line(x, 0, x, -9.5)),
      { t: "p", pts: arc(0, 7, 7, 180, 360, 16) },
      ...(feet ? [line(-7, 7, -9.5, 7), line(7, 7, 9.5, 7)] : []),
    ],
    0,
    1.3,
  );
/** Doppelte Ring-Darstellung für Taster. */
const pushbutton = (inner: Prim[] = []): Prim[] => [circle(0, 0, 6.5), circle(0, 0, 4.2), ...inner];

export const SYMBOLS: SymbolDef[] = [
  { id: "steckdose", name: "Steckdose (Schutzkontakt)", category: "Steckdosen", prims: socket([0]) },
  { id: "steckdose-doppel", name: "Steckdose 2-fach", category: "Steckdosen", prims: socket([-3, 3]) },
  { id: "steckdose-dreifach", name: "Steckdose 3-fach", category: "Steckdosen", prims: socket([-5, 0, 5]) },
  {
    id: "steckdose-schaltbar",
    name: "Steckdose, schaltbar",
    category: "Steckdosen",
    prims: [...socket([0]).filter((p) => !(p.t === "l" && p.x1 === 0 && p.x2 === 0)), line(0, 1.3, 0, -5), line(0, -5, 4, -9.5), line(4, -9.5, 8.5, -8)],
  },
  { id: "steckdose-usb", name: "Steckdose mit USB", category: "Steckdosen", prims: [...socket([2.5]), text("USB", 3.6, -5.2, -5.2)] },
  {
    id: "steckdose-drehstrom",
    name: "Drehstromsteckdose (CEE)",
    category: "Steckdosen",
    prims: [...socket([0]), line(-2, -4.5, 2, -6), line(-2, -6.5, 2, -8), line(-2, -2.5, 2, -4), text("3~", 4, 5.5, -6.5)],
  },

  { id: "schalter-aus", name: "Ausschalter", category: "Schalter", prims: shift([...switchCircle(), ...hook(1)], -2, 3.5) },
  { id: "schalter-aus-kontroll", name: "Ausschalter mit Kontrolllicht", category: "Schalter", prims: shift([...switchCircle(true), ...hook(1)], -2, 3.5) },
  { id: "schalter-serie", name: "Serienschalter", category: "Schalter", prims: shift([...switchCircle(), ...hook(2)], -2, 3.5) },
  { id: "schalter-wechsel", name: "Wechselschalter", category: "Schalter", prims: [...switchCircle(), ...hook(1), ...hook(1, 180)] },
  { id: "schalter-wechsel-kontroll", name: "Wechselschalter mit Kontrolllicht", category: "Schalter", prims: [...switchCircle(true), ...hook(1), ...hook(1, 180)] },
  { id: "schalter-kreuz", name: "Kreuzschalter", category: "Schalter", prims: [...switchCircle(), ...hook(1), ...hook(1, 180), ...hook(1, 0, true), ...hook(1, 180, true)] },
  { id: "taster", name: "Taster", category: "Schalter", prims: pushbutton() },
  { id: "taster-leucht", name: "Taster mit Kontrolllicht", category: "Schalter", prims: pushbutton([line(-3, -3, 3, 3), line(-3, 3, 3, -3)]) },
  {
    id: "dimmer",
    name: "Dimmer",
    category: "Schalter",
    prims: shift([circle(0, 0, 6.5), circle(0, 0, 4.2), { t: "p", pts: [[-5, -8], [5, -8], [5, -10]], closed: true }], 0, 1.5),
  },
  {
    id: "jalousie",
    name: "Jalousieschalter",
    category: "Schalter",
    prims: [
      { t: "r", x: -9, y: -6, w: 18, h: 12 },
      line(-3.5, 4, -3.5, -4), line(-3.5, -4, -5.5, -1.8), line(-3.5, -4, -1.5, -1.8),
      line(3.5, -4, 3.5, 4), line(3.5, 4, 1.5, 1.8), line(3.5, 4, 5.5, 1.8),
    ],
  },
  { id: "bewegungsmelder", name: "Bewegungsmelder", category: "Schalter", prims: [circle(0, 0, 7), text("BM", 6)] },

  { id: "leuchte-decke", name: "Deckenleuchte", category: "Leuchten", prims: [circle(0, 0, 6.5), line(-4.6, -4.6, 4.6, 4.6), line(-4.6, 4.6, 4.6, -4.6)] },
  { id: "leuchte-wand", name: "Wandleuchte", category: "Leuchten", prims: [circle(0, -1, 5), line(-3.5, -4.5, 3.5, 2.5), line(-3.5, 2.5, 3.5, -4.5), line(-8, 7, 8, 7)] },
  { id: "leuchte-feucht", name: "Feuchtraumleuchte", category: "Leuchten", prims: [{ t: "r", x: -9, y: -4, w: 18, h: 8 }, line(-9, 0, 9, 0)] },
  { id: "leuchte-notlicht", name: "Sicherheitsleuchte", category: "Leuchten", prims: [circle(0, 0, 6.5), line(-4.6, -4.6, 4.6, 4.6), line(-4.6, 4.6, 4.6, -4.6), { t: "r", x: -2.5, y: -2.5, w: 5, h: 5, f: true }] },

  { id: "verteiler", name: "Verteiler", category: "Verteiler und Zähler", prims: [{ t: "r", x: -9, y: -6, w: 18, h: 12, f: true }] },
  { id: "unterverteiler", name: "Unterverteiler", category: "Verteiler und Zähler", prims: [{ t: "r", x: -8, y: -5, w: 16, h: 10 }, line(-8, -5, 8, 5), line(-8, 5, 8, -5)] },
  { id: "zaehler", name: "Zählerschrank", category: "Verteiler und Zähler", prims: [{ t: "r", x: -9, y: -7, w: 18, h: 14 }, text("Z", 9)] },
  { id: "hak", name: "Hausanschlusskasten", category: "Verteiler und Zähler", prims: [{ t: "r", x: -9, y: -6, w: 18, h: 12 }, text("HAK", 5.5)] },

  { id: "daten", name: "Datendose (LAN)", category: "Kommunikation", prims: [{ t: "p", pts: [[-8, 7], [8, 7], [0, -8]], closed: true }, text("LAN", 4.2, 0, 3)] },
  { id: "telefon", name: "Telefondose", category: "Kommunikation", prims: [{ t: "p", pts: [[-8, 7], [8, 7], [0, -8]], closed: true }, text("T", 6, 0, 3)] },
  { id: "tv", name: "Antennendose (TV)", category: "Kommunikation", prims: [circle(0, 0, 7), text("TV", 6)] },
  { id: "klingel", name: "Klingel / Gong", category: "Kommunikation", prims: [circle(0, 0, 7), { t: "p", pts: arc(0, -1, 3.5, 180, 360, 8) }, line(-3.5, -1, 3.5, -1), circle(0, 3.5, 1, true)] },

  { id: "herd", name: "Herdanschluss", category: "Geräte", prims: [{ t: "r", x: -7, y: -7, w: 14, h: 14 }, circle(-3, -3, 1.8), circle(3, -3, 1.8), circle(-3, 3, 1.8), circle(3, 3, 1.8)] },
  { id: "anschluss", name: "Geräteanschluss", category: "Geräte", prims: [circle(0, 0, 6), circle(0, 0, 1.5, true), line(-9, 0, -6, 0), line(6, 0, 9, 0)] },
  { id: "rauchmelder", name: "Rauchmelder", category: "Geräte", prims: [circle(0, 0, 7), text("R", 8)] },
  { id: "wallbox", name: "Ladepunkt (Wallbox)", category: "Geräte", prims: [{ t: "r", x: -7, y: -8, w: 14, h: 16 }, text("E", 9)] },
];

export const SYMBOL_CATEGORIES = [...new Set(SYMBOLS.map((s) => s.category))];
/** Eintrag für Fenster und Türen in der Symbolauswahl. */
export const OPENING_CATEGORY = "Fenster und Türen";
const SYMBOL_MAP = new Map(SYMBOLS.map((s) => [s.id, s]));
export const symbolDef = (id: string): SymbolDef | undefined => SYMBOL_MAP.get(id);

/* ---------- Plandaten ---------- */

export const LINE_KINDS = {
  leitung: { name: "Leitung", dash: null as number[] | null },
  leerrohr: { name: "Leerrohr", dash: [6, 3] },
  daten: { name: "Datenleitung", dash: [1.5, 2.5] },
} as const;
export type LineKind = keyof typeof LINE_KINDS;

export const WALL_PRESETS = [
  { t: 36.5, name: "Außenwand 36,5 cm" },
  { t: 24, name: "Außenwand 24 cm" },
  { t: 17.5, name: "Innenwand 17,5 cm" },
  { t: 11.5, name: "Innenwand 11,5 cm" },
] as const;

const num = z.number().finite();
const coord = num.min(-2_000_000).max(2_000_000);
const id = z.string().min(1).max(40);
const label = z.string().max(80);

export const planDataSchema = z.object({
  v: z.literal(1).default(1),
  sheet: z.enum(["A4", "A3", "A2", "A1"]).default("A3"),
  portrait: z.boolean().default(false),
  scale: z.number().int().min(10).max(500).default(50),
  walls: z.array(z.object({ id, x1: coord, y1: coord, x2: coord, y2: coord, t: num.min(1).max(100) })).max(3000).default([]),
  rooms: z
    .array(z.object({ id, name: label, x: coord, y: coord, w: num.min(1).max(1_000_000), h: num.min(1).max(1_000_000), wallIds: z.array(id).max(8).optional(), height: num.min(150).max(1000).optional() }))
    .max(300)
    .default([]),
  symbols: z
    .array(
      z.object({
        id,
        type: z.string().max(40),
        x: coord,
        y: coord,
        rot: z.number().int().min(0).max(359).default(0),
        label: label.optional(),
        /** Montagehöhe über dem fertigen Fußboden in cm. */
        h: num.min(0).max(1000).optional(),
        /** Größe als Faktor (1 = Normalgröße). */
        k: num.min(0.3).max(4).optional(),
        /** An einer Wand angelegt: Wand, Abstand vom Wandanfang (Wandmitte) und Seite. */
        wallId: id.optional(),
        pos: num.min(0).max(2_000_000).optional(),
        side: z.union([z.literal(1), z.literal(-1)]).optional(),
      }),
    )
    .max(6000)
    .default([]),
  lines: z
    .array(
      z.object({
        id,
        kind: z.enum(["leitung", "leerrohr", "daten"]).default("leitung"),
        pts: z.array(z.tuple([coord, coord])).min(2).max(300),
        label: label.optional(),
      }),
    )
    .max(3000)
    .default([]),
  texts: z.array(z.object({ id, x: coord, y: coord, s: z.string().max(200), rot: z.number().int().min(0).max(359).default(0) })).max(600).default([]),
  openings: z
    .array(
      z.object({
        id,
        wallId: id,
        /** Abstand der Mitte vom Wandanfang in cm. */
        pos: num.min(0).max(2_000_000),
        w: num.min(20).max(1000),
        /** Höhe der Öffnung und Brüstungshöhe in cm (nur für die Wandansicht). */
        h: num.min(20).max(600).optional(),
        sill: num.min(0).max(600).optional(),
        kind: z.enum(["fenster", "tuer", "balkontuer", "doppeltuer", "durchgang"]),
        /** Aufschlag zur anderen Wandseite. */
        flip: z.boolean().default(false),
        /** Angeln auf der anderen Seite. */
        mirror: z.boolean().default(false),
      }),
    )
    .max(3000)
    .default([]),
  /** Leitungen in der Wandansicht: Punkte als [Abstand vom Wandanfang entlang der Wand, Höhe über Fußboden]. */
  cables: z
    .array(
      z.object({
        id,
        wallId: id,
        type: z.string().max(40).default("NYM-J 3x1,5"),
        pts: z.array(z.tuple([num.min(0).max(2_000_000), num.min(0).max(1000)])).min(2).max(100),
      }),
    )
    .max(3000)
    .default([]),
  /** Maße an den Wänden anzeigen. */
  dims: z.boolean().default(true),
  /** Raumhöhe in cm (Voreinstellung für Räume ohne eigene Höhe) und die Höhen der waagerechten Installationszonen über dem Fußboden; die Zone unter der Decke kommt aus der Raumhöhe dazu. */
  ceiling: num.min(150).max(1000).default(250),
  zones: z.array(num.min(0).max(1000)).max(8).default([30, 105]),
});
export type PlanData = z.infer<typeof planDataSchema>;
export type PlanWall = PlanData["walls"][number];
export type PlanRoom = PlanData["rooms"][number];
export type PlanSymbol = PlanData["symbols"][number];
export type PlanLine = PlanData["lines"][number];
export type PlanText = PlanData["texts"][number];
export type PlanOpening = PlanData["openings"][number];

export const emptyPlan = (): PlanData => planDataSchema.parse({});

export const planCreateSchema = z.object({ title: z.string().trim().min(1).max(150), sheet: z.enum(["A4", "A3", "A2", "A1"]).optional(), scale: z.number().int().min(10).max(500).optional() });
export const planUpdateSchema = z.object({ title: z.string().trim().min(1).max(150).optional(), data: planDataSchema.optional() });
export const planFileSchema = z.object({ folderId: z.string().uuid().optional() });
export const addonsSchema = z
  .object({ plans: z.boolean().optional(), billing: z.boolean().optional() })
  .refine((v) => v.plans !== undefined || v.billing !== undefined, "Bitte geben Sie an, welche Zusatzfunktion geändert werden soll.");

export interface PlanSummary {
  id: string;
  projectId: string;
  title: string;
  sheet: PlanSheet;
  scale: number;
  symbolCount: number;
  updatedAt: string;
  createdBy: string | null;
}
export interface PlanDetail extends PlanSummary {
  data: PlanData;
}
export interface Addons {
  plans: boolean;
}

/* ---------- Rechnen ---------- */

export const newId = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);

/** Größen in Zentimetern der Wirklichkeit, abhängig vom Maßstab. */
export const symbolSize = (scale: number) => (SYMBOL_MM * scale) / 10;
export const textSize = (scale: number) => (TEXT_MM * scale) / 10;

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Größenfaktor eines Symbols. */
export const symbolK = (s: PlanSymbol) => s.k ?? 1;

/** Wo ein Symbol im Grundriss steht: frei oder an seiner Wand (innen an der Wand, Abstand wie in der Wandansicht). */
export function symbolXY(d: PlanData, s: PlanSymbol): { x: number; y: number } {
  const wall = s.wallId ? d.walls.find((w) => w.id === s.wallId) : undefined;
  if (!wall || s.pos === undefined) return { x: s.x, y: s.y };
  const len = wallLength(wall);
  if (len < 0.01) return { x: s.x, y: s.y };
  const ux = (wall.x2 - wall.x1) / len;
  const uy = (wall.y2 - wall.y1) / len;
  const pos = Math.min(len, Math.max(0, s.pos));
  const off = (s.side ?? 1) * (wall.t / 2 + (symbolSize(d.scale) * symbolK(s)) / 2);
  return { x: wall.x1 + ux * pos - uy * off, y: wall.y1 + uy * pos + ux * off };
}

/**
 * Drehung eines Symbols im Grundriss in Grad: Bei einem Symbol an der Wand zeigt sein Strich (oben im Symbol) zur Wand,
 * dazu kommt die eigene Drehung.
 */
export function symbolAngle(d: PlanData, s: PlanSymbol): number {
  const wall = s.wallId ? d.walls.find((w) => w.id === s.wallId) : undefined;
  if (!wall || s.pos === undefined) return s.rot;
  const len = wallLength(wall);
  if (len < 0.01) return s.rot;
  const side = s.side ?? 1;
  const nx = (-(wall.y2 - wall.y1) / len) * side;
  const ny = ((wall.x2 - wall.x1) / len) * side;
  return Math.round((Math.atan2(-nx, ny) * 180) / Math.PI + s.rot + 360) % 360;
}

export function planBounds(d: PlanData): Bounds | null {
  let b: Bounds | null = null;
  const add = (x: number, y: number, pad = 0) => {
    b = b
      ? { minX: Math.min(b.minX, x - pad), minY: Math.min(b.minY, y - pad), maxX: Math.max(b.maxX, x + pad), maxY: Math.max(b.maxY, y + pad) }
      : { minX: x - pad, minY: y - pad, maxX: x + pad, maxY: y + pad };
  };
  const half = symbolSize(d.scale) / 2;
  for (const w of d.walls) {
    add(w.x1, w.y1, w.t / 2);
    add(w.x2, w.y2, w.t / 2);
  }
  for (const r of d.rooms) {
    add(r.x, r.y);
    add(r.x + r.w, r.y + r.h);
  }
  for (const s of d.symbols) {
    const p = symbolXY(d, s);
    add(p.x, p.y, half * symbolK(s));
  }
  for (const l of d.lines) for (const p of l.pts) add(p[0], p[1]);
  for (const t of d.texts) add(t.x, t.y, textSize(d.scale) * Math.max(1, t.s.length * 0.3));
  return b;
}

export interface SheetLayout {
  sheetW: number;
  sheetH: number;
  /** Zeichenfläche in Millimetern auf dem Blatt (links, oben, Breite, Höhe). */
  area: { x: number; y: number; w: number; h: number };
  legend: { x: number; y: number; w: number; h: number };
  titleBlock: { x: number; y: number; w: number; h: number };
  frame: { x: number; y: number; w: number; h: number };
}

export const SHEET_MARGIN = 10;
export const TITLE_BLOCK_H = 22;
export const LEGEND_W = 56;

/** Aufteilung des Blatts in Millimetern; Ursprung links oben. */
export function sheetLayout(sheet: PlanSheet, portrait: boolean): SheetLayout {
  const [lw, lh] = PLAN_SHEETS[sheet];
  const sheetW = portrait ? lh : lw;
  const sheetH = portrait ? lw : lh;
  const frame = { x: SHEET_MARGIN, y: SHEET_MARGIN, w: sheetW - 2 * SHEET_MARGIN, h: sheetH - 2 * SHEET_MARGIN };
  const titleBlock = { x: frame.x, y: frame.y + frame.h - TITLE_BLOCK_H, w: frame.w, h: TITLE_BLOCK_H };
  const bodyH = frame.h - TITLE_BLOCK_H;
  const legend = { x: frame.x + frame.w - LEGEND_W, y: frame.y, w: LEGEND_W, h: bodyH };
  const area = { x: frame.x + 4, y: frame.y + 4, w: frame.w - LEGEND_W - 8, h: bodyH - 8 };
  return { sheetW, sheetH, area, legend, titleBlock, frame };
}

/** Passt die Zeichnung beim gewählten Maßstab auf das Blatt? */
export function fitsSheet(d: PlanData): { fits: boolean; widthMm: number; heightMm: number } {
  const b = planBounds(d);
  const lay = sheetLayout(d.sheet, d.portrait);
  if (!b) return { fits: true, widthMm: 0, heightMm: 0 };
  const widthMm = ((b.maxX - b.minX) * 10) / d.scale;
  const heightMm = ((b.maxY - b.minY) * 10) / d.scale;
  return { fits: widthMm <= lay.area.w && heightMm <= lay.area.h, widthMm, heightMm };
}

export const polylineLength = (pts: [number, number][]) => pts.reduce((sum, p, i) => (i ? sum + Math.hypot(p[0] - pts[i - 1]![0], p[1] - pts[i - 1]![1]) : 0), 0);

export interface LegendEntry {
  symbolId: string;
  name: string;
  count: number;
}
export interface PlanLegend {
  symbols: LegendEntry[];
  lines: { kind: LineKind; name: string; meters: number }[];
}

export function planLegend(d: PlanData): PlanLegend {
  const counts = new Map<string, number>();
  for (const s of d.symbols) counts.set(s.type, (counts.get(s.type) ?? 0) + 1);
  const symbols = SYMBOLS.filter((s) => counts.has(s.id)).map((s) => ({ symbolId: s.id, name: s.name, count: counts.get(s.id)! }));
  const meters = new Map<LineKind, number>();
  for (const l of d.lines) meters.set(l.kind, (meters.get(l.kind) ?? 0) + polylineLength(l.pts) / 100);
  const lines = (Object.keys(LINE_KINDS) as LineKind[]).filter((k) => meters.has(k)).map((k) => ({ kind: k, name: LINE_KINDS[k].name, meters: Math.round(meters.get(k)! * 10) / 10 }));
  return { symbols, lines };
}

export const roomArea = (r: PlanRoom) => Math.round(((r.w * r.h) / 10000) * 10) / 10;

/** Rastert auf `step` Zentimeter. */
export const snap = (v: number, step: number) => Math.round(v / step) * step;

/** Nächster Fangpunkt: erst Wand-/Linienenden, dann Raster. */
export function snapPoint(d: PlanData, x: number, y: number, grid: number, tol: number): [number, number] {
  let best: [number, number] | null = null;
  let bestDist = tol;
  const test = (px: number, py: number) => {
    const dist = Math.hypot(px - x, py - y);
    if (dist < bestDist) {
      bestDist = dist;
      best = [px, py];
    }
  };
  for (const w of d.walls) {
    test(w.x1, w.y1);
    test(w.x2, w.y2);
  }
  for (const l of d.lines) for (const p of l.pts) test(p[0], p[1]);
  for (const sy of d.symbols) {
    const p = symbolXY(d, sy);
    test(p.x, p.y);
  }
  for (const r of d.rooms) {
    test(r.x, r.y);
    test(r.x + r.w, r.y + r.h);
  }
  return best ?? [snap(x, grid), snap(y, grid)];
}

export function distToSegment(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / len2));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

export type PlanRef = { kind: "symbol" | "wall" | "line" | "text" | "room" | "opening"; id: string };

/** Was liegt an dieser Stelle? Obenauf liegende Dinge (Symbole, Text) zuerst. */
export function hitTest(d: PlanData, x: number, y: number, tol: number): PlanRef | null {
  const half = symbolSize(d.scale) / 2 + tol * 0.3;
  for (const s of [...d.symbols].reverse()) {
    const p = symbolXY(d, s);
    const hs = half * symbolK(s);
    if (Math.abs(p.x - x) <= hs && Math.abs(p.y - y) <= hs) return { kind: "symbol", id: s.id };
  }
  const th = textSize(d.scale);
  for (const t of [...d.texts].reverse()) if (x >= t.x - tol && x <= t.x + t.s.length * th * 0.6 + tol && Math.abs(y - t.y) <= th + tol) return { kind: "text", id: t.id };
  for (const l of [...d.lines].reverse()) for (let i = 1; i < l.pts.length; i++) if (distToSegment(x, y, l.pts[i - 1]![0], l.pts[i - 1]![1], l.pts[i]![0], l.pts[i]![1]) <= tol) return { kind: "line", id: l.id };
  for (const o of [...d.openings].reverse()) {
    const g = openingGeom(d, o);
    if (!g) continue;
    const along = (x - g.cx) * g.ux + (y - g.cy) * g.uy;
    const across = (x - g.cx) * g.nx + (y - g.cy) * g.ny;
    if (Math.abs(along) <= o.w / 2 && Math.abs(across) <= Math.max(tol, g.wall.t / 2)) return { kind: "opening", id: o.id };
  }
  for (const w of [...d.walls].reverse()) if (distToSegment(x, y, w.x1, w.y1, w.x2, w.y2) <= Math.max(tol, w.t / 2)) return { kind: "wall", id: w.id };
  for (const r of [...d.rooms].reverse()) if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) return { kind: "room", id: r.id };
  return null;
}

/** Verschiebt ein Element um dx/dy. */
export function moveRef(d: PlanData, ref: PlanRef, dx: number, dy: number): PlanData {
  switch (ref.kind) {
    case "symbol":
      return {
        ...d,
        symbols: d.symbols.map((s) => {
          if (s.id !== ref.id) return s;
          const wall = s.wallId ? d.walls.find((w) => w.id === s.wallId) : undefined;
          if (wall && s.pos !== undefined) {
            // An der Wand angelegte Symbole wandern nur entlang der Wand.
            const len = wallLength(wall);
            const along = (dx * (wall.x2 - wall.x1) + dy * (wall.y2 - wall.y1)) / Math.max(len, 0.01);
            return { ...s, pos: Math.min(len, Math.max(0, s.pos + along)) };
          }
          return { ...s, x: s.x + dx, y: s.y + dy };
        }),
      };
    case "text":
      return { ...d, texts: d.texts.map((s) => (s.id === ref.id ? { ...s, x: s.x + dx, y: s.y + dy } : s)) };
    case "wall":
      return { ...d, walls: d.walls.map((w) => (w.id === ref.id ? { ...w, x1: w.x1 + dx, y1: w.y1 + dy, x2: w.x2 + dx, y2: w.y2 + dy } : w)) };
    case "room":
      return { ...d, rooms: d.rooms.map((r) => (r.id === ref.id ? { ...r, x: r.x + dx, y: r.y + dy } : r)) };
    case "line":
      return { ...d, lines: d.lines.map((l) => (l.id === ref.id ? { ...l, pts: l.pts.map((p) => [p[0] + dx, p[1] + dy] as [number, number]) } : l)) };
    case "opening":
      return {
        ...d,
        openings: d.openings.map((o) => {
          const g = o.id === ref.id ? openingGeom(d, o) : null;
          if (!g) return o;
          return { ...o, pos: clampPos(o.pos + dx * g.ux + dy * g.uy, o.w, g.len) };
        }),
      };
  }
}

export function removeRef(d: PlanData, ref: PlanRef): PlanData {
  const keep = <T extends { id: string }>(list: T[]) => list.filter((x) => x.id !== ref.id);
  switch (ref.kind) {
    case "symbol":
      return { ...d, symbols: keep(d.symbols) };
    case "text":
      return { ...d, texts: keep(d.texts) };
    case "wall":
      return {
        ...d,
        walls: keep(d.walls),
        openings: d.openings.filter((o) => o.wallId !== ref.id),
        symbols: d.symbols.filter((s) => s.wallId !== ref.id),
        cables: d.cables.filter((c) => c.wallId !== ref.id),
        rooms: d.rooms.map((r) => (r.wallIds?.includes(ref.id) ? { ...r, wallIds: undefined } : r)),
      };
    case "room":
      return { ...d, rooms: keep(d.rooms) };
    case "line":
      return { ...d, lines: keep(d.lines) };
    case "opening":
      return { ...d, openings: keep(d.openings) };
  }
}

/** Dreht ein Symbol oder einen Text in 90°-Schritten. */
export function rotateRef(d: PlanData, ref: PlanRef): PlanData {
  if (ref.kind === "symbol") return { ...d, symbols: d.symbols.map((s) => (s.id === ref.id ? { ...s, rot: (s.rot + 90) % 360 } : s)) };
  if (ref.kind === "text") return { ...d, texts: d.texts.map((s) => (s.id === ref.id ? { ...s, rot: (s.rot + 90) % 360 } : s)) };
  // Bei Fenstern und Türen wechselt „Drehen“ die Aufschlagrichtung.
  if (ref.kind === "opening") return { ...d, openings: d.openings.map((o) => (o.id === ref.id ? (o.flip ? { ...o, flip: false, mirror: !o.mirror } : { ...o, flip: true }) : o)) };
  return d;
}

/** Kopie mit Versatz, liefert die neue Referenz. */
export function duplicateRef(d: PlanData, ref: PlanRef, offset: number): { data: PlanData; ref: PlanRef } {
  const nid = newId();
  switch (ref.kind) {
    case "opening":
      return { data: d, ref };
    case "symbol": {
      const s = d.symbols.find((x) => x.id === ref.id);
      if (!s) return { data: d, ref };
      const copy = s.wallId && s.pos !== undefined ? { ...s, id: nid, pos: s.pos + offset } : { ...s, id: nid, x: s.x + offset, y: s.y + offset };
      return { data: { ...d, symbols: [...d.symbols, copy] }, ref: { kind: "symbol", id: nid } };
    }
    case "text": {
      const s = d.texts.find((x) => x.id === ref.id);
      return s ? { data: { ...d, texts: [...d.texts, { ...s, id: nid, x: s.x + offset, y: s.y + offset }] }, ref: { kind: "text", id: nid } } : { data: d, ref };
    }
    case "wall": {
      const s = d.walls.find((x) => x.id === ref.id);
      return s ? { data: { ...d, walls: [...d.walls, { ...s, id: nid, x1: s.x1 + offset, y1: s.y1 + offset, x2: s.x2 + offset, y2: s.y2 + offset }] }, ref: { kind: "wall", id: nid } } : { data: d, ref };
    }
    case "room": {
      const s = d.rooms.find((x) => x.id === ref.id);
      return s ? { data: { ...d, rooms: [...d.rooms, { ...s, id: nid, x: s.x + offset, y: s.y + offset }] }, ref: { kind: "room", id: nid } } : { data: d, ref };
    }
    case "line": {
      const s = d.lines.find((x) => x.id === ref.id);
      return s ? { data: { ...d, lines: [...d.lines, { ...s, id: nid, pts: s.pts.map((p) => [p[0] + offset, p[1] + offset] as [number, number]) }] }, ref: { kind: "line", id: nid } } : { data: d, ref };
    }
  }
}

/** Entfernt Symbole mit unbekannter Art, damit alte oder fehlerhafte Pläne nie das PDF verhindern. */
export function cleanPlan(raw: unknown): PlanData {
  const d = planDataSchema.parse(raw ?? {});
  const walls = new Set(d.walls.map((w) => w.id));
  return {
    ...d,
    symbols: d.symbols.filter((s) => SYMBOL_MAP.has(s.type) && (!s.wallId || walls.has(s.wallId))),
    openings: d.openings.filter((o) => walls.has(o.wallId)),
    cables: d.cables.filter((c) => walls.has(c.wallId)),
  };
}

/* ---------- Wände: Längen und Maße ---------- */

export const wallLength = (w: PlanWall) => Math.hypot(w.x2 - w.x1, w.y2 - w.y1);

/** Länge in Metern mit Komma, z. B. „4,85 m“. */
export const formatMeters = (cm: number) => `${(Math.round(cm) / 100).toFixed(2).replace(".", ",")} m`;

/**
 * Liest eine eingetippte Länge. Ohne Komma sind es Zentimeter („485“), mit Komma oder Punkt Meter („4,85“);
 * ein Zusatz „m“ oder „cm“ ist erlaubt. Liefert Zentimeter oder null.
 */
export function parseLength(input: string): number | null {
  const t = input.trim().toLowerCase().replace(/\s+/g, "");
  const m = /^(\d+(?:[.,]\d+)?)(cm|m)?$/.exec(t);
  if (!m) return null;
  const n = Number(m[1]!.replace(",", "."));
  if (!Number.isFinite(n)) return null;
  const meters = m[2] === "m" || (m[2] !== "cm" && /[.,]/.test(m[1]!));
  const cm = Math.round((meters ? n * 100 : n) * 10) / 10;
  return cm >= 1 && cm <= 100_000 ? cm : null;
}

export function clampPos(pos: number, w: number, len: number): number {
  if (len <= w) return len / 2;
  return Math.min(len - w / 2, Math.max(w / 2, pos));
}

/**
 * Setzt die Länge einer Wand. Der Anfang bleibt stehen, das Ende wandert. Angeschlossene Wände gehen mit: Eine
 * rechtwinklig angeschlossene Wand rückt als Ganzes nach, die Wand an ihrem anderen Ende wird verlängert, so bleibt
 * ein Rechteck ein Rechteck. Fenster und Türen behalten ihre Stelle im Raum und bleiben in der Wand.
 */
export function setWallLength(d: PlanData, wallId: string, lengthCm: number): PlanData {
  const w = d.walls.find((x) => x.id === wallId);
  if (!w || lengthCm < 1) return d;
  const len = wallLength(w);
  if (len < 0.01) return d;
  const ux = (w.x2 - w.x1) / len;
  const uy = (w.y2 - w.y1) / len;
  const dx = ux * (lengthCm - len);
  const dy = uy * (lengthCm - len);
  const near = (ax: number, ay: number, bx: number, by: number) => Math.hypot(ax - bx, ay - by) < 0.6;

  const shifted = new Set<string>();
  const farEnds: [number, number][] = [];
  const walls = d.walls.map((x) => {
    if (x.id === wallId) return { ...x, x2: x.x2 + dx, y2: x.y2 + dy };
    const atStart = near(x.x1, x.y1, w.x2, w.y2);
    const atEnd = near(x.x2, x.y2, w.x2, w.y2);
    if (!atStart && !atEnd) return x;
    const l = Math.hypot(x.x2 - x.x1, x.y2 - x.y1);
    const perpendicular = l > 0.01 && Math.abs(((x.x2 - x.x1) / l) * ux + ((x.y2 - x.y1) / l) * uy) < 0.02;
    if (perpendicular) {
      shifted.add(x.id);
      farEnds.push(atStart ? [x.x2, x.y2] : [x.x1, x.y1]);
      return { ...x, x1: x.x1 + dx, y1: x.y1 + dy, x2: x.x2 + dx, y2: x.y2 + dy };
    }
    return atStart ? { ...x, x1: x.x1 + dx, y1: x.y1 + dy } : { ...x, x2: x.x2 + dx, y2: x.y2 + dy };
  });
  // Wände am anderen Ende einer nachgerückten Wand werden mitgezogen.
  const stretched = walls.map((x, i) => {
    const orig = d.walls[i]!;
    if (x.id === wallId || shifted.has(x.id) || x !== orig) return x;
    let out = x;
    for (const f of farEnds) {
      if (near(orig.x1, orig.y1, f[0], f[1])) out = { ...out, x1: out.x1 + dx, y1: out.y1 + dy };
      if (near(orig.x2, orig.y2, f[0], f[1])) out = { ...out, x2: out.x2 + dx, y2: out.y2 + dy };
    }
    return out;
  });
  const next = { ...d, walls: stretched };
  const startShiftOf = (wallId: string) => {
    const before = d.walls.find((x) => x.id === wallId);
    const after = stretched.find((x) => x.id === wallId);
    const g = after ? wallLength(after) : 0;
    if (!before || !after || g < 0.01) return null;
    const sx = (after.x2 - after.x1) / g;
    const sy = (after.y2 - after.y1) / g;
    return { shift: (after.x1 - before.x1) * sx + (after.y1 - before.y1) * sy, len: g };
  };
  return {
    ...next,
    symbols: d.symbols.map((sy) => {
      const st = sy.wallId && sy.pos !== undefined ? startShiftOf(sy.wallId) : null;
      return st && sy.pos !== undefined ? { ...sy, pos: Math.min(st.len, Math.max(0, sy.pos - st.shift)) } : sy;
    }),
    openings: d.openings.map((o) => {
      const before = d.walls.find((x) => x.id === o.wallId);
      const after = stretched.find((x) => x.id === o.wallId);
      if (!before || !after) return o;
      const g = openingGeom(next, o);
      if (!g) return o;
      // Hat sich der Wandanfang bewegt, bleibt die Stelle im Raum gleich.
      const startShift = (after.x1 - before.x1) * g.ux + (after.y1 - before.y1) * g.uy;
      return { ...o, pos: clampPos(o.pos - startShift, o.w, g.len) };
    }),
  };
}

export interface WallDim {
  id: string;
  x: number;
  y: number;
  /** Winkel in Grad (y nach unten), immer lesbar von links nach rechts. */
  angle: number;
  text: string;
}

/**
 * Wie weit die Wand an jedem Ende von angeschlossenen, quer dazu laufenden Wänden „aufgebraucht“ wird
 * (halbe Dicke dieser Wand). Daraus ergibt sich das lichte Maß zwischen den Wandflächen.
 */
export function wallJoins(d: PlanData, w: PlanWall): { a0: number; a1: number } {
  const len = wallLength(w);
  if (len < 0.01) return { a0: 0, a1: 0 };
  const ux = (w.x2 - w.x1) / len;
  const uy = (w.y2 - w.y1) / len;
  let a0 = 0;
  let a1 = 0;
  for (const o of d.walls) {
    if (o.id === w.id) continue;
    const l = wallLength(o);
    if (l < 0.01) continue;
    const cross = Math.abs(((o.x2 - o.x1) / l) * ux + ((o.y2 - o.y1) / l) * uy) < 0.5;
    if (!cross) continue;
    const touches = (px: number, py: number, qx: number, qy: number) => Math.hypot(px - qx, py - qy) < 0.6;
    if (touches(o.x1, o.y1, w.x1, w.y1) || touches(o.x2, o.y2, w.x1, w.y1)) a0 = Math.max(a0, o.t / 2);
    if (touches(o.x1, o.y1, w.x2, w.y2) || touches(o.x2, o.y2, w.x2, w.y2)) a1 = Math.max(a1, o.t / 2);
  }
  return { a0, a1 };
}

/** Lichte Länge der Wand zwischen den Wandflächen (so wird sie im Plan angeschrieben). */
export function wallInner(d: PlanData, w: PlanWall): number {
  const { a0, a1 } = wallJoins(d, w);
  return Math.max(0, wallLength(w) - a0 - a1);
}

/** Setzt die lichte Länge einer Wand (siehe setWallLength für das Nachrücken der Nachbarn). */
export function setWallInner(d: PlanData, wallId: string, innerCm: number): PlanData {
  const w = d.walls.find((x) => x.id === wallId);
  if (!w) return d;
  const { a0, a1 } = wallJoins(d, w);
  return syncRooms(setWallLength(d, wallId, innerCm + a0 + a1));
}

/** Maßzahlen an den Wänden, außen an der Wand, parallel zu ihr. */
export function wallDims(d: PlanData): WallDim[] {
  const b = planBounds(d);
  if (!b) return [];
  const cx = (b.minX + b.maxX) / 2;
  const cy = (b.minY + b.maxY) / 2;
  const th = textSize(d.scale);
  const out: WallDim[] = [];
  for (const w of d.walls) {
    const len = wallLength(w);
    if (len < 30) continue;
    const ux = (w.x2 - w.x1) / len;
    const uy = (w.y2 - w.y1) / len;
    let nx = -uy;
    let ny = ux;
    const mx = (w.x1 + w.x2) / 2;
    const my = (w.y1 + w.y2) / 2;
    if ((mx - cx) * nx + (my - cy) * ny < 0) {
      nx = -nx;
      ny = -ny;
    }
    const off = w.t / 2 + th * 0.9;
    let angle = (Math.atan2(uy, ux) * 180) / Math.PI;
    if (angle > 90) angle -= 180;
    if (angle <= -90) angle += 180;
    out.push({ id: w.id, x: mx + nx * off, y: my + ny * off, angle, text: formatMeters(wallInner(d, w)) });
  }
  return out;
}

/* ---------- Fenster und Türen ---------- */

export function openingGeom(d: PlanData, o: PlanOpening) {
  const wall = d.walls.find((w) => w.id === o.wallId);
  if (!wall) return null;
  const len = wallLength(wall);
  if (len < 0.01) return null;
  const ux = (wall.x2 - wall.x1) / len;
  const uy = (wall.y2 - wall.y1) / len;
  return { wall, len, ux, uy, nx: -uy, ny: ux, cx: wall.x1 + ux * o.pos, cy: wall.y1 + uy * o.pos };
}

/** Wand und Stelle darauf, die einem Punkt am nächsten liegen. */
export function nearestWall(d: PlanData, x: number, y: number, tol: number): { wall: PlanWall; pos: number } | null {
  let best: { wall: PlanWall; pos: number } | null = null;
  let bestDist = Infinity;
  for (const w of d.walls) {
    const dist = distToSegment(x, y, w.x1, w.y1, w.x2, w.y2);
    if (dist > Math.max(tol, w.t / 2) || dist >= bestDist) continue;
    const len = wallLength(w);
    if (len < 1) continue;
    bestDist = dist;
    best = { wall: w, pos: ((x - w.x1) * (w.x2 - w.x1) + (y - w.y1) * (w.y2 - w.y1)) / len };
  }
  return best;
}

export interface OpeningShapes {
  /** Lücke in der Wand (Viereck), wird mit Papierfarbe gefüllt. */
  gap: [number, number][];
  /** Linien und Bögen in Weltkoordinaten. */
  strokes: [number, number][][];
}

/** Zeichenelemente eines Fensters oder einer Tür in Weltkoordinaten, für Bildschirm und PDF gleich. */
export function openingShapes(d: PlanData, o: PlanOpening): OpeningShapes | null {
  const g = openingGeom(d, o);
  if (!g) return null;
  const { ux, uy, nx, ny, cx, cy } = g;
  const t = g.wall.t;
  const P = (a: number, b: number): [number, number] => [cx + ux * a + nx * b, cy + uy * a + ny * b];
  const hw = o.w / 2;
  const ht = t / 2;
  const gap: [number, number][] = [P(-hw, -ht - 0.3), P(hw, -ht - 0.3), P(hw, ht + 0.3), P(-hw, ht + 0.3)];
  const strokes: [number, number][][] = [];
  const jambs = () => strokes.push([P(-hw, -ht), P(-hw, ht)], [P(hw, -ht), P(hw, ht)]);
  const side = o.flip ? -1 : 1;

  const leaf = (hingeA: number, width: number, sweepToward: 1 | -1) => {
    // Angel bei `hingeA` entlang der Wand; das Türblatt steht offen im rechten Winkel zur Wand.
    const hinge = P(hingeA, 0);
    const tip = P(hingeA, side * width);
    const closed = P(hingeA + sweepToward * width, 0);
    strokes.push([hinge, tip]);
    const a0 = Math.atan2(tip[1] - hinge[1], tip[0] - hinge[0]);
    let a1 = Math.atan2(closed[1] - hinge[1], closed[0] - hinge[0]);
    let diff = a1 - a0;
    while (diff > Math.PI) diff -= 2 * Math.PI;
    while (diff < -Math.PI) diff += 2 * Math.PI;
    a1 = a0 + diff;
    const n = 14;
    strokes.push(Array.from({ length: n + 1 }, (_, i) => [hinge[0] + width * Math.cos(a0 + ((a1 - a0) * i) / n), hinge[1] + width * Math.sin(a0 + ((a1 - a0) * i) / n)] as [number, number]));
  };

  switch (o.kind) {
    case "fenster":
      strokes.push([P(-hw, -ht), P(hw, -ht), P(hw, ht), P(-hw, ht), P(-hw, -ht)], [P(-hw, -ht / 3), P(hw, -ht / 3)], [P(-hw, ht / 3), P(hw, ht / 3)]);
      break;
    case "tuer":
      jambs();
      leaf(o.mirror ? hw : -hw, o.w, o.mirror ? -1 : 1);
      break;
    case "balkontuer":
      jambs();
      strokes.push([P(-hw, 0), P(hw, 0)]);
      leaf(o.mirror ? hw : -hw, o.w, o.mirror ? -1 : 1);
      break;
    case "doppeltuer":
      jambs();
      leaf(-hw, hw, 1);
      leaf(hw, hw, -1);
      break;
    case "durchgang":
      jambs();
      break;
  }
  return { gap, strokes };
}

/* ---------- Räume aus Maßen ---------- */

/** Räume, die aus vier Wänden entstanden sind, folgen den Wänden (Fläche = lichte Fläche). */
export function syncRooms(d: PlanData): PlanData {
  if (!d.rooms.some((r) => r.wallIds?.length === 4)) return d;
  return {
    ...d,
    rooms: d.rooms.map((r) => {
      if (r.wallIds?.length !== 4) return r;
      const [top, right, bottom, left] = r.wallIds.map((id) => d.walls.find((w) => w.id === id));
      if (!top || !right || !bottom || !left) return r;
      const x = Math.min(left.x1, left.x2) + left.t / 2;
      const y = Math.min(top.y1, top.y2) + top.t / 2;
      const x2 = Math.max(right.x1, right.x2) - right.t / 2;
      const y2 = Math.max(bottom.y1, bottom.y2) - bottom.t / 2;
      return x2 - x < 1 || y2 - y < 1 ? r : { ...r, x, y, w: x2 - x, h: y2 - y };
    }),
  };
}

export type RoomBasis = "innen" | "aussen" | "mitte";

/**
 * Legt einen rechteckigen Raum mit vier Wänden an. `w` ist die Breite (links nach rechts), `h` die Tiefe (oben nach
 * unten); je nach `basis` sind es die lichten Maße, die Außenmaße oder die Maße bis Wandmitte.
 * Ohne Position kommt der Raum rechts neben die vorhandene Zeichnung.
 */
export function createRoom(d: PlanData, o: { name: string; w: number; h: number; t: number; basis: RoomBasis; height?: number; x?: number; y?: number }): { data: PlanData; roomId: string; wallIds: string[] } {
  const t = o.t;
  const inner = o.basis === "innen" ? { w: o.w, h: o.h } : o.basis === "aussen" ? { w: o.w - 2 * t, h: o.h - 2 * t } : { w: o.w - t, h: o.h - t };
  const b = planBounds(d);
  const x = o.x ?? (b ? Math.ceil((b.maxX + 100) / 10) * 10 : 0);
  const y = o.y ?? (b ? Math.floor(b.minY / 10) * 10 : 0);
  // (x, y) ist die linke obere Ecke der lichten Fläche.
  const L = x - t / 2;
  const T = y - t / 2;
  const R = x + inner.w + t / 2;
  const B = y + inner.h + t / 2;
  const ids = [newId(), newId(), newId(), newId()];
  const walls: PlanWall[] = [
    { id: ids[0]!, x1: L, y1: T, x2: R, y2: T, t },
    { id: ids[1]!, x1: R, y1: T, x2: R, y2: B, t },
    { id: ids[2]!, x1: R, y1: B, x2: L, y2: B, t },
    { id: ids[3]!, x1: L, y1: B, x2: L, y2: T, t },
  ];
  const roomId = newId();
  const room: PlanRoom = { id: roomId, name: o.name, x, y, w: inner.w, h: inner.h, wallIds: ids, ...(o.height ? { height: o.height } : {}) };
  return { data: { ...d, walls: [...d.walls, ...walls], rooms: [...d.rooms, room] }, roomId, wallIds: ids };
}

/* ---------- Wandansicht: Inhalte je Wand ---------- */

export const OPENING_DEFAULTS: Record<OpeningKind, { h: number; sill: number }> = {
  fenster: { h: 125, sill: 90 },
  tuer: { h: 201, sill: 0 },
  balkontuer: { h: 225, sill: 0 },
  doppeltuer: { h: 201, sill: 0 },
  durchgang: { h: 201, sill: 0 },
};

/** Rahmen der Wandansicht: lichte Länge und der Abstand der Innenfläche zum Wandanfang. */
export function wallFrame(d: PlanData, wallId: string) {
  const wall = d.walls.find((w) => w.id === wallId);
  if (!wall) return null;
  const { a0, a1 } = wallJoins(d, wall);
  const length = wallLength(wall);
  return { wall, a0, a1, length, inner: Math.max(0, length - a0 - a1) };
}

/** Raumhöhe der Wand: die Höhe des Raums, zu dem sie gehört, sonst die Voreinstellung des Blatts. */
export function wallHeight(d: PlanData, wallId: string): number {
  return d.rooms.find((r) => r.wallIds?.includes(wallId))?.height ?? d.ceiling;
}

/** Installationszonen (Mitte, cm über Fußboden): die eingestellten plus 30 cm unter der Decke. */
export function zoneHeights(d: PlanData, height: number): { h: number; label: string }[] {
  const list = d.zones.filter((z) => z < height).map((z) => ({ h: z, label: `${z} cm` }));
  const top = height - 30;
  if (top > 0 && !list.some((z) => Math.abs(z.h - top) < 1)) list.push({ h: top, label: "30 cm unter Decke" });
  return list.sort((a, b) => a.h - b.h);
}

/** In der Wandansicht wird von innen auf die Wand geschaut; bei manchen Wänden läuft die Zeichenrichtung dann von rechts nach links. */
export const wallViewFlipped = (d: PlanData, w: PlanWall): boolean => insideSide(d, w) === -1;

/** Welche Seite der Wand „innen“ ist: die zum Mittelpunkt der Zeichnung (oder zum Raum der Wand). */
export function insideSide(d: PlanData, w: PlanWall): 1 | -1 {
  const len = wallLength(w);
  if (len < 0.01) return 1;
  const room = d.rooms.find((r) => r.wallIds?.includes(w.id));
  const b = planBounds(d);
  const cx = room ? room.x + room.w / 2 : b ? (b.minX + b.maxX) / 2 : w.x1;
  const cy = room ? room.y + room.h / 2 : b ? (b.minY + b.maxY) / 2 : w.y1;
  const nx = -(w.y2 - w.y1) / len;
  const ny = (w.x2 - w.x1) / len;
  return (cx - (w.x1 + w.x2) / 2) * nx + (cy - (w.y1 + w.y2) / 2) * ny >= 0 ? 1 : -1;
}

/** Symbol an eine Wand setzen. `x` ist der Abstand von der Wandfläche links in der Ansicht, `h` die Montagehöhe. */
export function attachSymbol(d: PlanData, wallId: string, type: string, x: number, h: number): { data: PlanData; id: string } | null {
  const f = wallFrame(d, wallId);
  if (!f || !symbolDef(type)) return null;
  const id = newId();
  const pos = Math.min(f.length, Math.max(0, x + f.a0));
  const sym: PlanSymbol = { id, type, x: f.wall.x1, y: f.wall.y1, rot: 0, h, wallId, pos, side: insideSide(d, f.wall) };
  return { data: { ...d, symbols: [...d.symbols, sym] }, id };
}

/**
 * Mehrere gleiche Symbole gleichmäßig auf einer Wand verteilen (Abstand vom Rand `margin`), in einer Höhe.
 * Stellen, die in einer Tür oder einem Fenster liegen würden, werden ausgelassen.
 */
export function distributeSymbols(d: PlanData, wallId: string, type: string, count: number, h: number, margin: number): { data: PlanData; added: number } {
  const f = wallFrame(d, wallId);
  if (!f || count < 1 || !symbolDef(type)) return { data: d, added: 0 };
  const span = Math.max(0, f.inner - 2 * margin);
  const xs = count === 1 ? [f.inner / 2] : Array.from({ length: count }, (_, i) => margin + (span * i) / (count - 1));
  const blocked = d.openings
    .filter((o) => o.wallId === wallId && (o.kind !== "fenster" || (o.sill ?? OPENING_DEFAULTS.fenster.sill) <= h))
    .map((o) => [o.pos - f.a0 - o.w / 2, o.pos - f.a0 + o.w / 2] as const);
  let cur = d;
  let added = 0;
  for (const x of xs) {
    if (blocked.some(([a, b]) => x >= a - 5 && x <= b + 5)) continue;
    const r = attachSymbol(cur, wallId, type, x, h);
    if (r) {
      cur = r.data;
      added++;
    }
  }
  return { data: cur, added };
}

/** Abstände einer Öffnung zu den Ecken, so wie man sie in der Wandansicht von innen sieht (links / rechts, lichte Fläche). */
export function openingEdges(d: PlanData, o: PlanOpening): { left: number; right: number; inner: number } | null {
  const f = wallFrame(d, o.wallId);
  if (!f) return null;
  const start = o.pos - o.w / 2 - f.a0;
  const left = wallViewFlipped(d, f.wall) ? f.inner - start - o.w : start;
  return { left, right: f.inner - left - o.w, inner: f.inner };
}

/** Setzt den Abstand der Öffnung zur linken Ecke (Blick von innen). */
export function setOpeningLeft(d: PlanData, openingId: string, left: number): PlanData {
  const o = d.openings.find((x) => x.id === openingId);
  const f = o ? wallFrame(d, o.wallId) : null;
  if (!o || !f) return d;
  const l = Math.min(Math.max(0, left), Math.max(0, f.inner - o.w));
  const start = wallViewFlipped(d, f.wall) ? f.inner - l - o.w : l;
  return { ...d, openings: d.openings.map((x) => (x.id === openingId ? { ...x, pos: start + o.w / 2 + f.a0 } : x)) };
}

export const CABLE_TYPES = ["NYM-J 3x1,5", "NYM-J 3x2,5", "NYM-J 5x1,5", "NYM-J 5x2,5", "NYM-J 5x4", "Leerrohr", "Datenleitung"] as const;

/**
 * „Magnet“ für Symbole: Jedes Symbol belegt ein Quadrat (`half` = halbe Seitenlänge). Kommt ein Symbol einem anderen
 * nah, rastet es so ein, dass sich die Quadrate seitlich oder oben/unten genau berühren, und richtet sich dabei auf der
 * anderen Achse an der Mitte des Nachbarn aus.
 */
export function magnetSnap(x: number, y: number, half: number, others: { x: number; y: number; half: number }[], tol: number): [number, number] {
  let h: { cost: number; x: number; y: number } | null = null;
  let v: { cost: number; x: number; y: number } | null = null;
  for (const o of others) {
    const gap = o.half + half;
    const dx = x - o.x;
    const dy = y - o.y;
    if (Math.abs(dy) < gap) {
      const cost = Math.abs(Math.abs(dx) - gap);
      if (cost <= tol && (!h || cost < h.cost)) h = { cost, x: o.x + Math.sign(dx || 1) * gap, y: Math.abs(dy) <= tol ? o.y : y };
    }
    if (Math.abs(dx) < gap) {
      const cost = Math.abs(Math.abs(dy) - gap);
      if (cost <= tol && (!v || cost < v.cost)) v = { cost, x: Math.abs(dx) <= tol ? o.x : x, y: o.y + Math.sign(dy || 1) * gap };
    }
  }
  const best = h && v ? (h.cost <= v.cost ? h : v) : (h ?? v);
  return best ? [round(best.x), round(best.y)] : [x, y];
}

/** Magnet im Grundriss: rastet ein Symbol (Größenfaktor `k`) an den Quadraten der anderen Symbole ein. */
export function magnetSymbol(d: PlanData, x: number, y: number, k: number, ignoreId: string | undefined, tol: number): [number, number] {
  const unit = symbolSize(d.scale);
  const others = d.symbols
    .filter((s) => s.id !== ignoreId)
    .map((s) => ({ ...symbolXY(d, s), half: (unit * symbolK(s)) / 2 }));
  return magnetSnap(x, y, (unit * k) / 2, others, tol);
}
